<template>
    <div class="editor rounded">
        <slot name="top"/>
        <template v-if="readonly">
            <div class="wysiwyg-content border border-gray-400 p-4" v-html="input"></div>
        </template>
        <template v-else>
            <tinymce
                ref="editor"
                :model-value="input"
                :d="id"
                :init="editorConfig"
                @update:model-value="input = $event"
            />
            <input :id="id" ref="imageInput" :accept="allowed.join()" hidden type="file" @change="uploadFile($event)">
        </template>
        <slot name="bottom"/>
        <div v-show="openSidebar" :class="{'z-1500': openSidebar}" class="fixed inset-0 overflow-hidden">
            <div class="absolute inset-0 overflow-hidden">
                <div class="absolute inset-0 bg-gray-500/75 transition-opacity"></div>
                <section class="absolute inset-y-0 right-0 pl-10 max-w-full flex">
                    <div class="relative w-screen max-w-md">
                        <div class="absolute top-0 left-0 -ml-8 pt-4 pr-2 flex sm:-ml-10 sm:pr-4">
                            <button
                                aria-label="Close panel"
                                class="text-gray-100 hover:text-white transition ease-in-out duration-150"
                                type="button"
                                @click="closeSidebar"
                            >
                                <svg-vue class="h-6 w-6" icon="font-awesome.times-light"/>
                            </button>
                        </div>
                        <div class="h-full flex flex-col pt-6 bg-white shadow-xl">
                            <header class="px-4 sm:px-6 pb-6 border-b border-gray-200">
                                <h2 class="text-lg leading-7 font-medium text-gray-900">
                                    <template v-if="sidebar.shortCodeSelect">{{ $t('Short codes') }}</template>
                                    <template v-if="sidebar.cannedReplySelect">{{ $t('Canned replies') }}</template>
                                </h2>
                            </header>
                            <div class="relative flex-1 overflow-y-scroll">
                                <template v-if="sidebar.shortCodeSelect">
                                    // CONTENT
                                </template>
                                <template v-if="sidebar.cannedReplySelect">
                                    <template v-if="cannedReplyList.length > 0">
                                        <ul class="divide-y divide-gray-200 overflow-y-auto">
                                            <template v-for="cannedReply in cannedReplyList">
                                                <li class="px-6 py-5 relative hover:bg-gray-100 cursor-pointer" @click="setContent(cannedReply.body)">
                                                    <div class="text-sm leading-5 font-medium text-gray-900 truncate">
                                                        {{ cannedReply.name }}
                                                    </div>
                                                    <div class="text-sm leading-5 text-gray-500 truncate">
                                                        {{ cannedReply.body }}
                                                    </div>
                                                </li>
                                            </template>
                                        </ul>
                                    </template>
                                    <template v-else>
                                        <empty-state :title="$t('No records found')" />
                                    </template>
                                </template>
                            </div>
                        </div>
                    </div>
                </section>
            </div>
        </div>
    </div>
</template>

<script>
import "tinymce/tinymce";
import "tinymce/icons/default";
import "tinymce/themes/silver";
import "tinymce/plugins/paste";
import "tinymce/plugins/link";
import "tinymce/plugins/image";
import "tinymce/plugins/table";
import "tinymce/plugins/lists";
import "tinymce/plugins/code";
import "tinymce/plugins/codesample";
import "tinymce/plugins/fullscreen";
import "tinymce/plugins/autoresize";
import Editor from '@tinymce/tinymce-vue';
import {resolveStoreAccess} from "@/utils/access-context";
import {UI_COLORS} from '@/utils/ui-colors';

const EDITOR_ICON_STROKE = encodeURIComponent(UI_COLORS.text.default);

export default {
    name: "input-wysiwyg",
    emits: ['input', 'update:modelValue'],
    components: {
        'tinymce': Editor,
    },
    props: {
        modelValue: {
            required: false,
            default: undefined,
        },
        value: {
            required: false
        },
        id: {
            type: String,
            required: true
        },
        required: {
            type: Boolean,
            default: false
        },
        readonly: {
            type: Boolean,
            default: false
        },
        allowed: {
            type: Array,
            default: function () {
                return ['image/png', 'image/x-citrix-png', 'image/x-png', 'image/jpeg', 'image/x-citrix-jpeg'];
            }
        },
        plugins: {
            type: Object,
            default: function () {
                return {images: true};
            }
        },
        cannedReplyList: {
            type: Array,
            default: function () {
                return [];
            }
        },
        minHeight: {
            type: Number,
            default: 180
        }
    },
    computed: {
        input: {
            get() {
                return this.modelValue !== undefined ? this.modelValue : this.value;
            },
            set(value) {
                this.$emit('update:modelValue', value);
                this.$emit('input', value);
            }
        },
        openSidebar() {
            return this.sidebar.cannedReplySelect || this.sidebar.shortCodeSelect;
        }
    },
    data() {
        const self = this;
        const isGerman = String(document.documentElement.lang || '').toLowerCase().startsWith('de');
        const t = (en, de) => isGerman ? de : en;
        const headingToolbar = self.plugins.headings ? 'headingTitle headingSubtitle | ' : '';
        const headingContentStyles = self.plugins.headings ? `
                    h1 {
                        font-size: 1.5rem;
                        line-height: 2rem;
                        font-weight: 600;
                        margin: 0 0 0.75rem 0;
                    }
                    h2 {
                        font-size: 1.25rem;
                        line-height: 1.75rem;
                        font-weight: 600;
                        margin: 0 0 0.625rem 0;
                    }
                ` : '';
        const accessContext = resolveStoreAccess(self.$store);
        const isCustomerFromRoute = !!(self.$route && Array.isArray(self.$route.matched)
            && self.$route.matched.some(function (record) {
                return !!(record && record.meta && record.meta.requires_customer_scope);
            }));
        const isCustomerUser = !!accessContext.is_customer || isCustomerFromRoute;
        const toolbarGroups = [
            'undo redo',
            (headingToolbar + 'bold italic underline').trim(),
            'numlist bullist',
            ('upload tableInsert attachment link cannedReply shortCode codeSampleCustom').trim(),
        ];

        if (self.plugins.menuPoint) {
            toolbarGroups.push('menuPoint');
        }

        if (!isCustomerUser) {
            toolbarGroups.push('code');
        }

        toolbarGroups.push('fullscreen');
        return {
            editorConfig: {

                branding: false,
                add_unload_trigger: false,
                browser_spellcheck: true,
                paste_as_text: false,
                paste_data_images: true,
                automatic_uploads: false,
                statusbar: false,
                menubar: false,
                convert_urls: false,
                forced_root_block: false,
                element_format: 'html',
                default_link_target: '_blank',
                link_assume_external_targets: true,
                target_list: false,
                link_title: false,
                language: 'en',
                mobile: {
                    theme: 'silver'
                },

                codesample_languages: [
                    {text: 'HTML/XML', value: 'markup'},
                    {text: 'XML', value: 'markup'},
                    {text: 'JavaScript', value: 'javascript'},
                    {text: 'JSON', value: 'javascript'},
                    {text: 'CSS', value: 'css'},
                    {text: 'PHP', value: 'php'},
                    // {text: 'Ruby', value: 'ruby'},
                    {text: 'Python', value: 'python'},
                    // {text: 'Java', value: 'java'},
                    // {text: 'C', value: 'c'},
                    // {text: 'C#', value: 'csharp'},
                    // {text: 'C++', value: 'cpp'}
                ],

                advlist_bullet_styles: [{
                        title: 'image bullets',
                        styles: {
                          listStyleImage: "url('url/to/image.png')"
                        }
                      }, {
                        title: 'Default',
                        styles: {
                          listStyleType: '',
                          listStyleImage: ""
                        }
                      }, {
                        title: 'Circle',
                        styles: {
                          listStyleType: 'circle',
                          listStyleImage: ""
                        }
                      }, {
                        title: 'Disc',
                        styles: {
                          listStyleType: 'disc',
                          listStyleImage: ""
                        }
                      }, {
                        title: 'Square',
                        styles: {
                          listStyleType: 'square',
                          listStyleImage: ""
                        }
                      }],

                contextmenu_never_use_native: true,
                contextmenu: 'customLink tableCustomMenu',
                plugins: 'paste image table link code lists fullscreen codesample autoresize',
                toolbar: toolbarGroups.join(' | '),
                min_height: self.minHeight,
                autoresize_min_height: self.minHeight,
                table_toolbar: '',
                table_default_attributes: {
                    border: '0'
                },
                table_default_styles: {
                    width: '100%',
                    borderCollapse: 'collapse'
                },
                content_style: `
                    table { border-collapse: collapse; width: 100%; }
                    table th, table td,
                    table.mce-item-table th, table.mce-item-table td {
                        border: 1px solid ${UI_COLORS.border.default} !important;
                        border-style: solid !important;
                        padding: 8px 10px;
                        vertical-align: top;
                        font-size: 13px;
                        line-height: 1.4;
                    }
                    table thead th,
                    table.mce-item-table thead th,
                    table:not(:has(thead)) tbody tr:first-child th,
                    table:not(:has(thead)) tbody tr:first-child td,
                    table.mce-item-table:not(:has(thead)) tbody tr:first-child th,
                    table.mce-item-table:not(:has(thead)) tbody tr:first-child td {
                        background: ${UI_COLORS.surface.raised} !important;
                        font-weight: 400 !important;
                        text-align: left !important;
                        font-size: 13px;
                        line-height: 1.4;
                    }
                    .doc-menu-anchor-chip {
                        display: inline-flex;
                        align-items: center;
                        border: 1px solid ${UI_COLORS.border.default};
                        border-radius: 4px;
                        padding: 2px 8px;
                    }
                    .doc-menu-anchor-chip::before {
                        content: '';
                        display: inline-block;
                        vertical-align: middle;
                        margin-right: 6px;
                        width: 14px;
                        height: 14px;
                        background-repeat: no-repeat;
                        background-position: center;
                        background-size: 14px 14px;
                        background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath d='M6 3h12a1 1 0 0 1 1 1v17l-7-4-7 4V4a1 1 0 0 1 1-1z' stroke='${EDITOR_ICON_STROKE}' stroke-width='1.8' stroke-linecap='round' stroke-linejoin='round' fill='none'/%3E%3C/svg%3E");
                    }
                    .doc-menu-anchor-chip.doc-menu-anchor-heading-h1 {
                        font-size: 1.5rem;
                        line-height: 2rem;
                        font-weight: 600;
                    }
                    .doc-menu-anchor-chip.doc-menu-anchor-heading-h2 {
                        font-size: 1.25rem;
                        line-height: 1.75rem;
                        font-weight: 600;
                    }
                    pre.code-sample-plain,
                    pre[class*="language-"] {
                        background: ${UI_COLORS.surface.raised};
                        border: 1px solid ${UI_COLORS.border.default};
                        border-radius: 4px;
                        padding: 12px 14px;
                        overflow: auto;
                        font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
                        font-size: 13px;
                        line-height: 1.5;
                    }
                    .token.comment { color: ${UI_COLORS.text.muted}; }
                    .token.tag, .token.selector { color: #be185d; }
                    .token.attr-name, .token.keyword { color: #0f766e; }
                    .token.attr-value, .token.string { color: #0369a1; }
                    .token.number, .token.boolean, .token.constant { color: #7c3aed; }
                    pre.line-numbers {
                        position: relative;
                        padding-left: 3.5rem !important;
                    }
                    pre.line-numbers > code {
                        white-space: pre;
                    }
                    pre.line-numbers .line-numbers-rows {
                        position: absolute;
                        top: 12px;
                        left: 0;
                        width: 2.5rem;
                        border-right: 1px solid ${UI_COLORS.border.default};
                        user-select: none;
                        pointer-events: none;
                        text-align: right;
                        color: ${UI_COLORS.text.subtle};
                        font-size: 12px;
                        line-height: 1.5;
                    }
                    pre.line-numbers .line-numbers-rows > span {
                        display: block;
                    }
                    pre.line-numbers .line-numbers-rows > span::before {
                        content: counter(line-number);
                        counter-increment: line-number;
                        display: block;
                        padding-right: 0.5rem;
                    }
                    pre.line-numbers .line-numbers-rows {
                        counter-reset: line-number;
                    }
                    ${headingContentStyles}
                `,
                setup: function (editor) {
                    const findCodeSamplePre = function (nodeParam) {
                        const node = nodeParam || editor.selection.getNode();
                        return editor.dom.getParent(node, 'pre[data-code-language],pre[data-syntax-highlight],pre[class*="language-"],pre.code-sample-plain');
                    };
                    const getCodeSampleLanguageClass = function (className) {
                        const classAttr = String(className || '');
                        const match = classAttr.match(/(?:^|\s)language-([a-z0-9_-]+)/i);
                        return match ? match[1] : '';
                    };
                    const removeLineNumberRows = function (pre) {
                        if (!pre) {
                            return;
                        }
                        const rows = pre.querySelector('.line-numbers-rows');
                        if (rows) {
                            rows.remove();
                        }
                    };
                    const normalizeCodeSampleVisuals = function (rootNode) {
                        const root = rootNode || editor.getBody();
                        if (!root) {
                            return;
                        }
                        const preBlocks = root.querySelectorAll('pre');
                        preBlocks.forEach(function (pre) {
                            const codeNode = pre.querySelector('code');
                            const languageFromClass = getCodeSampleLanguageClass(pre.className) || getCodeSampleLanguageClass(codeNode ? codeNode.className : '');
                            const hasLanguageClass = languageFromClass.length > 0;
                            const hasCodeChild = !!codeNode;
                            const hasDataFlag = pre.hasAttribute('data-syntax-highlight')
                                || pre.hasAttribute('data-line-numbers')
                                || pre.hasAttribute('data-code-language')
                                || (codeNode && (codeNode.hasAttribute('data-syntax-highlight')
                                    || codeNode.hasAttribute('data-line-numbers')
                                    || codeNode.hasAttribute('data-code-language')));
                            if (!hasLanguageClass && !hasCodeChild && !hasDataFlag) {
                                return;
                            }

                            if (!pre.hasAttribute('data-syntax-highlight')) {
                                if (codeNode && codeNode.hasAttribute('data-syntax-highlight')) {
                                    pre.setAttribute('data-syntax-highlight', codeNode.getAttribute('data-syntax-highlight'));
                                } else {
                                    pre.setAttribute('data-syntax-highlight', hasLanguageClass ? '1' : '0');
                                }
                            }
                            if (!pre.hasAttribute('data-line-numbers')) {
                                if (codeNode && codeNode.hasAttribute('data-line-numbers')) {
                                    pre.setAttribute('data-line-numbers', codeNode.getAttribute('data-line-numbers'));
                                } else {
                                    pre.setAttribute('data-line-numbers', pre.classList.contains('line-numbers') ? '1' : '0');
                                }
                            }
                            if (!pre.hasAttribute('data-code-language') && languageFromClass) {
                                pre.setAttribute('data-code-language', languageFromClass);
                            } else if (!pre.hasAttribute('data-code-language') && codeNode && codeNode.getAttribute('data-code-language')) {
                                pre.setAttribute('data-code-language', codeNode.getAttribute('data-code-language'));
                            }

                            const syntaxEnabled = pre.getAttribute('data-syntax-highlight') === '1';
                            const lineNumbersEnabled = pre.getAttribute('data-line-numbers') === '1';
                            const language = pre.getAttribute('data-code-language') || languageFromClass || 'markup';

                            if (syntaxEnabled) {
                                pre.classList.remove('code-sample-plain');
                                if (!getCodeSampleLanguageClass(pre.className)) {
                                    pre.classList.add('language-' + language);
                                }
                            } else {
                                pre.className = String(pre.className || '')
                                    .split(/\s+/)
                                    .filter(function (token) {
                                        return token && !/^language-/i.test(token);
                                    })
                                    .join(' ');
                                pre.classList.add('code-sample-plain');
                            }

                            if (lineNumbersEnabled) {
                                pre.classList.add('line-numbers');
                                removeLineNumberRows(pre);
                                const codeNode = pre.querySelector('code') || pre;
                                const text = codeNode.textContent || '';
                                const normalizedText = text.replace(/\r\n?/g, '\n');
                                const endsWithNewline = /\n$/.test(normalizedText);
                                const lineCount = Math.max(normalizedText.split('\n').length - (endsWithNewline ? 1 : 0), 1);
                                const rows = editor.getDoc().createElement('span');
                                rows.className = 'line-numbers-rows';
                                for (let i = 0; i < lineCount; i += 1) {
                                    rows.appendChild(editor.getDoc().createElement('span'));
                                }
                                pre.appendChild(rows);
                            } else {
                                pre.classList.remove('line-numbers');
                                removeLineNumberRows(pre);
                            }
                        });
                    };
                    const openCodeSampleDialog = function () {
                        const languageItems = self.editorConfig.codesample_languages || [
                            {text: 'HTML/XML', value: 'markup'},
                            {text: 'JavaScript', value: 'javascript'},
                            {text: 'CSS', value: 'css'},
                            {text: 'PHP', value: 'php'},
                            {text: 'Python', value: 'python'},
                        ];
                        const fallbackLanguage = (languageItems[0] && languageItems[0].value) ? languageItems[0].value : 'markup';
                        const existingPre = findCodeSamplePre();
                        const existingCodeNode = existingPre ? existingPre.querySelector('code') : null;
                        const existingCode = existingPre ? (existingCodeNode ? existingCodeNode.textContent : existingPre.textContent) : '';
                        const existingLanguage = existingPre
                            ? (existingPre.getAttribute('data-code-language') || getCodeSampleLanguageClass(existingPre.className) || fallbackLanguage)
                            : fallbackLanguage;
                        const existingSyntaxEnabled = existingPre
                            ? (existingPre.getAttribute('data-syntax-highlight') !== '0')
                            : false;
                        const existingLineNumbers = existingPre
                            ? (existingPre.getAttribute('data-line-numbers') === '1' || existingPre.classList.contains('line-numbers'))
                            : false;

                        editor.windowManager.open({
                            title: t('Insert/Edit Code Sample', 'Codebeispiel einfuegen/bearbeiten'),
                            size: 'large',
                            body: {
                                type: 'panel',
                                items: [
                                    {type: 'selectbox', name: 'language', label: t('Language', 'Sprache'), items: languageItems},
                                    {type: 'textarea', name: 'code', label: t('Code view', 'Codeansicht')},
                                    {type: 'checkbox', name: 'syntaxHighlighting', label: t('Enable syntax highlighting', 'Syntax-Highlighting aktivieren')},
                                    {type: 'checkbox', name: 'lineNumbers', label: t('Show line numbers', 'Zeilennummern anzeigen')}
                                ]
                            },
                            buttons: [
                                {type: 'cancel', name: 'cancel', text: t('Cancel', 'Abbrechen')},
                                {type: 'submit', name: 'save', text: t('Save', 'Speichern'), primary: true}
                            ],
                            initialData: {
                                language: existingLanguage,
                                syntaxHighlighting: existingSyntaxEnabled,
                                lineNumbers: existingLineNumbers,
                                code: existingCode
                            },
                            onSubmit: function (api) {
                                const data = api.getData();
                                const language = String(data.language || fallbackLanguage).trim();
                                const code = String(data.code || '');
                                const syntaxHighlighting = !!data.syntaxHighlighting;
                                const lineNumbers = !!data.lineNumbers;
                                const codeHtml = editor.dom.encode(code);
                                const classList = [];
                                const codeClassList = [];
                                if (syntaxHighlighting) {
                                    classList.push('language-' + language);
                                    codeClassList.push('language-' + language);
                                } else {
                                    classList.push('code-sample-plain');
                                }
                                if (lineNumbers) {
                                    classList.push('line-numbers');
                                }
                                const preHtml = '<pre class="' + classList.join(' ') + '" data-code-language="' + editor.dom.encode(language) + '" data-syntax-highlight="' + (syntaxHighlighting ? '1' : '0') + '" data-line-numbers="' + (lineNumbers ? '1' : '0') + '"><code class="' + codeClassList.join(' ') + '">' + codeHtml + '</code></pre>';
                                editor.undoManager.transact(function () {
                                    if (existingPre) {
                                        editor.dom.setOuterHTML(existingPre, preHtml);
                                    } else {
                                        editor.insertContent(preHtml);
                                    }
                                    editor.fire('SetContent');
                                    normalizeCodeSampleVisuals();
                                });
                                api.close();
                            }
                        });
                    };
                    const getActiveMenuAnchor = function () {
                        const node = editor.selection.getNode();
                        let chip = editor.dom.getParent(node, '.doc-menu-anchor-chip');
                        if (chip) {
                            return chip;
                        }

                        const range = editor.selection.getRng();
                        if (range && range.startContainer) {
                            chip = editor.dom.getParent(range.startContainer, '.doc-menu-anchor-chip');
                            if (chip) {
                                return chip;
                            }
                        }

                        return null;
                    };
                    const isInsideMenuAnchor = function () {
                        return !!getActiveMenuAnchor();
                    };
                    const moveCaretToNewParagraphAfterAnchor = function (chipParam) {
                        const chip = chipParam || getActiveMenuAnchor();
                        if (!chip) {
                            return;
                        }

                        const parentParagraph = editor.dom.getParent(chip, 'p');
                        if (!parentParagraph) {
                            return;
                        }

                        const newParagraph = editor.dom.create('p', {}, '<br data-mce-bogus="1">');
                        editor.dom.insertAfter(newParagraph, parentParagraph);
                        editor.selection.select(newParagraph, true);
                        editor.selection.collapse(true);
                    };
                    const sanitizeAnchorNode = function (anchor) {
                        const allowedInlineTags = new Set(['B', 'STRONG', 'I', 'EM', 'U', 'S', 'SPAN', 'SMALL', 'MARK', 'SUB', 'SUP', 'CODE', 'A']);
                        const applyAnchorHeadingLevel = function (level) {
                            anchor.classList.remove('doc-menu-anchor-heading-h1');
                            anchor.classList.remove('doc-menu-anchor-heading-h2');
                            if (level === 'h1' || level === 'h2') {
                                anchor.classList.add('doc-menu-anchor-heading-' + level);
                                anchor.setAttribute('data-doc-anchor-heading', level);
                            } else {
                                anchor.removeAttribute('data-doc-anchor-heading');
                            }
                        };

                        const nestedAnchors = anchor.querySelectorAll('.doc-menu-anchor-chip');
                        nestedAnchors.forEach(function (nested) {
                            if (nested !== anchor) {
                                nested.remove();
                            }
                        });

                        const descendants = Array.from(anchor.querySelectorAll('*'));
                        descendants.forEach(function (node) {
                            if (node === anchor) {
                                return;
                            }

                            if (node.tagName === 'H1') {
                                applyAnchorHeadingLevel('h1');
                            }
                            if (node.tagName === 'H2') {
                                applyAnchorHeadingLevel('h2');
                            }

                            if (!allowedInlineTags.has(node.tagName)) {
                                const parent = node.parentNode;
                                if (!parent) {
                                    return;
                                }

                                while (node.firstChild) {
                                    parent.insertBefore(node.firstChild, node);
                                }
                                parent.removeChild(node);
                                return;
                            }

                            if (node.tagName === 'A') {
                                const href = node.getAttribute('href') || '';
                                if (href.trim() === '') {
                                    node.removeAttribute('href');
                                }
                            }
                        });

                        const breaks = anchor.querySelectorAll('br');
                        breaks.forEach(function (br) {
                            br.remove();
                        });

                        const hasVisibleText = (anchor.textContent || '').replace(/\s+/g, ' ').trim().length > 0;
                        if (!hasVisibleText) {
                            anchor.innerHTML = 'Titel des Menues';
                        }
                    };
                    const normalizeMenuAnchors = function (rootNode) {
                        const root = rootNode || editor.getBody();
                        if (!root) {
                            return;
                        }

                        const anchors = root.querySelectorAll('.doc-menu-anchor-chip');
                        anchors.forEach(function (anchor) {
                            sanitizeAnchorNode(anchor);
                        });
                    };
                    const sanitizeNestedMenuAnchorsInHtml = function (html) {
                        if (!html || html.indexOf('doc-menu-anchor-chip') === -1) {
                            return html;
                        }

                        const container = document.createElement('div');
                        container.innerHTML = html;
                        const anchors = container.querySelectorAll('.doc-menu-anchor-chip');
                        anchors.forEach(function (anchor) {
                            sanitizeAnchorNode(anchor);
                        });

                        return container.innerHTML;
                    };
                    const setHeadingForSelection = function (level) {
                        const chip = getActiveMenuAnchor();
                        if (chip) {
                            editor.undoManager.transact(function () {
                                const isSameLevel = chip.classList.contains('doc-menu-anchor-heading-' + level);
                                chip.classList.remove('doc-menu-anchor-heading-h1');
                                chip.classList.remove('doc-menu-anchor-heading-h2');
                                chip.removeAttribute('data-doc-anchor-heading');
                                if (!isSameLevel && (level === 'h1' || level === 'h2')) {
                                    chip.classList.add('doc-menu-anchor-heading-' + level);
                                    chip.setAttribute('data-doc-anchor-heading', level);
                                }
                                sanitizeAnchorNode(chip);
                            });
                            return;
                        }

                        const node = editor.selection.getNode();
                        const currentHeading = editor.dom.getParent(node, 'h1,h2');
                        const isSameHeadingLevel = !!(currentHeading && String(currentHeading.tagName || '').toLowerCase() === level);

                        editor.undoManager.transact(function () {
                            if (isSameHeadingLevel) {
                                editor.execCommand('FormatBlock', false, 'p');
                            } else {
                                editor.execCommand('FormatBlock', false, level);
                            }
                            normalizeHeadingInlineFormattingInRoot();
                        });
                    };
                    const isInsideHeadingNode = function (nodeParam) {
                        const node = nodeParam || editor.selection.getNode();
                        return !!editor.dom.getParent(node, 'h1,h2');
                    };
                    const moveCaretToNewParagraphAfterHeading = function () {
                        const heading = editor.dom.getParent(editor.selection.getNode(), 'h1,h2');
                        if (!heading) {
                            return;
                        }

                        const newParagraph = editor.dom.create('p', {}, '<br data-mce-bogus="1">');
                        editor.dom.insertAfter(newParagraph, heading);
                        editor.selection.select(newParagraph, true);
                        editor.selection.collapse(true);
                    };
                    const unwrapNode = function (node) {
                        const parent = node && node.parentNode;
                        if (!parent) {
                            return;
                        }
                        while (node.firstChild) {
                            parent.insertBefore(node.firstChild, node);
                        }
                        parent.removeChild(node);
                    };
                    const normalizeHeadingInlineFormattingInRoot = function (rootNode) {
                        const root = rootNode || editor.getBody();
                        if (!root) {
                            return;
                        }
                        const inlineFormattingInHeadings = root.querySelectorAll('h1 b, h1 strong, h1 i, h1 em, h1 u, h2 b, h2 strong, h2 i, h2 em, h2 u');
                        inlineFormattingInHeadings.forEach(function (node) {
                            unwrapNode(node);
                        });
                    };
                    const sanitizeHeadingInlineFormattingInHtml = function (html) {
                        if (!html || !/<h[12]\b/i.test(html) || !/<(b|strong|i|em|u)\b/i.test(html)) {
                            return html;
                        }
                        const container = document.createElement('div');
                        container.innerHTML = html;
                        normalizeHeadingInlineFormattingInRoot(container);
                        return container.innerHTML;
                    };

                    editor.ui.registry.addMenuItem('customLink', {
                        text: t('Link...', 'Link...'),
                        onAction: function () {
                            editor.execCommand('mceLink');
                        }
                    });
                    editor.ui.registry.addButton('codeSampleCustom', {
                        icon: 'code-sample',
                        tooltip: t('Insert/edit code sample', 'Codebeispiel einfuegen/bearbeiten'),
                        onAction: function () {
                            openCodeSampleDialog();
                        }
                    });
                    editor.addCommand('codesample', function () {
                        openCodeSampleDialog();
                    });
                    editor.on('init', function () {
                        const doc = editor.getDoc();
                        if (!doc) {
                            return;
                        }
                        doc.addEventListener('dblclick', function (event) {
                            const pre = findCodeSamplePre(event.target);
                            if (!pre) {
                                return;
                            }
                            event.preventDefault();
                            event.stopPropagation();
                            if (event.stopImmediatePropagation) {
                                event.stopImmediatePropagation();
                            }
                            editor.selection.select(pre, true);
                            openCodeSampleDialog();
                        }, true);
                        normalizeHeadingInlineFormattingInRoot();
                        normalizeCodeSampleVisuals();
                        normalizeMenuAnchors();
                    });
                    editor.on('LoadContent', function () {
                        normalizeHeadingInlineFormattingInRoot();
                        normalizeCodeSampleVisuals();
                        normalizeMenuAnchors();
                    });

                    editor.ui.registry.addButton('tableInsert', {
                        icon: 'table',
                        tooltip: t('Table', 'Tabelle'),
                        onAction: function () {
                            editor.undoManager.transact(function () {
                                editor.insertContent(`
                                    <table style="width:100%;border-collapse:collapse;">
                                        <thead>
                                            <tr>
                                                <th>&nbsp;</th>
                                                <th>&nbsp;</th>
                                                <th>&nbsp;</th>
                                                <th>&nbsp;</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr>
                                                <td>&nbsp;</td>
                                                <td>&nbsp;</td>
                                                <td>&nbsp;</td>
                                                <td>&nbsp;</td>
                                            </tr>
                                            <tr>
                                                <td>&nbsp;</td>
                                                <td>&nbsp;</td>
                                                <td>&nbsp;</td>
                                                <td>&nbsp;</td>
                                            </tr>
                                            <tr>
                                                <td>&nbsp;</td>
                                                <td>&nbsp;</td>
                                                <td>&nbsp;</td>
                                                <td>&nbsp;</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                `);
                            });
                        }
                    });

                    editor.ui.registry.addMenuItem('customTableInsertRowBefore', {
                        text: t('Insert row before', 'Zeile davor einfuegen'),
                        onAction: function () {
                            editor.execCommand('mceTableInsertRowBefore');
                        }
                    });
                    editor.ui.registry.addMenuItem('customTableInsertRowAfter', {
                        text: t('Insert row after', 'Zeile danach einfuegen'),
                        onAction: function () {
                            editor.execCommand('mceTableInsertRowAfter');
                        }
                    });
                    editor.ui.registry.addMenuItem('customTableDeleteRow', {
                        text: t('Delete row', 'Zeile löschen'),
                        onAction: function () {
                            editor.execCommand('mceTableDeleteRow');
                        }
                    });
                    editor.ui.registry.addMenuItem('customTableInsertColBefore', {
                        text: t('Insert column before', 'Spalte davor einfuegen'),
                        onAction: function () {
                            editor.execCommand('mceTableInsertColBefore');
                        }
                    });
                    editor.ui.registry.addMenuItem('customTableInsertColAfter', {
                        text: t('Insert column after', 'Spalte danach einfuegen'),
                        onAction: function () {
                            editor.execCommand('mceTableInsertColAfter');
                        }
                    });
                    editor.ui.registry.addMenuItem('customTableDeleteCol', {
                        text: t('Delete column', 'Spalte löschen'),
                        onAction: function () {
                            editor.execCommand('mceTableDeleteCol');
                        }
                    });

                    editor.ui.registry.addMenuItem('customTableDelete', {
                        text: t('Delete table', 'Tabelle löschen'),
                        onAction: function () {
                            editor.execCommand('mceTableDelete');
                        }
                    });

                    editor.ui.registry.addNestedMenuItem('customTableRowMenu', {
                        text: t('Row', 'Zeile'),
                        getSubmenuItems: function () {
                            return 'customTableInsertRowBefore customTableInsertRowAfter customTableDeleteRow';
                        }
                    });
                    editor.ui.registry.addNestedMenuItem('customTableColumnMenu', {
                        text: t('Column', 'Spalte'),
                        getSubmenuItems: function () {
                            return 'customTableInsertColBefore customTableInsertColAfter customTableDeleteCol';
                        }
                    });
                    editor.ui.registry.addContextMenu('tableCustomMenu', {
                        update: function (element) {
                            const contextNode = element || editor.selection.getNode();
                            const isInsideTable = !!editor.dom.getParent(contextNode, 'table');
                            if (!isInsideTable) {
                                return '';
                            }
                            return 'customTableRowMenu customTableColumnMenu customTableDelete';
                        }
                    });

                    editor.on('BeforeExecCommand', function (event) {
                        if (!event || !event.command) {
                            return;
                        }
                        const command = String(event.command).toLowerCase();
                        const blockedInHeading = command === 'bold'
                            || command === 'mcebold'
                            || command === 'togglebold'
                            || command === 'italic'
                            || command === 'mceitalic'
                            || command === 'toggleitalic'
                            || command === 'underline'
                            || command === 'mceunderline'
                            || command === 'toggleunderline';
                        if (!blockedInHeading || !isInsideHeadingNode()) {
                            return;
                        }
                        event.preventDefault();
                    });

                    editor.on('BeforeSetContent', function (event) {
                        if (!event || typeof event.content !== 'string') {
                            return;
                        }
                        event.content = sanitizeHeadingInlineFormattingInHtml(event.content);
                    });
                    editor.on('PreProcess', function (event) {
                        if (!event || !event.node) {
                            return;
                        }
                        const rows = event.node.querySelectorAll ? event.node.querySelectorAll('.line-numbers-rows') : [];
                        rows.forEach(function (node) {
                            node.remove();
                        });
                    });

                    editor.on('SetContent', function () {
                        normalizeHeadingInlineFormattingInRoot();
                        normalizeCodeSampleVisuals();
                    });

                    editor.on('input', function () {
                        normalizeHeadingInlineFormattingInRoot();
                        normalizeCodeSampleVisuals();
                    });
                    editor.on('keydown', function (event) {
                        if ((event.key === 'Enter' || event.keyCode === 13) && !event.shiftKey && isInsideHeadingNode()) {
                            event.preventDefault();
                            moveCaretToNewParagraphAfterHeading();
                        }
                    });

                    if (self.plugins.images) {
                        editor.ui.registry.addButton('upload', {
                            icon: 'image',
                            onAction: function (_) {
                                self.$refs.imageInput.click();
                            }
                        });
                    }
                    if (self.plugins.cannedReply) {
                        editor.ui.registry.addButton('cannedReply', {
                            icon: 'comment-add',
                            onAction: function (_) {
                                alert('Working');
                            }
                        });
                    }
                    if (self.plugins.cannedReply) {
                        editor.ui.registry.addButton('cannedReply', {
                            icon: 'comment-add',
                            onAction: function (_) {
                                self.sidebar.cannedReplySelect = true;
                            }
                        });
                    }
                    if (self.plugins.attachment) {
                        editor.ui.registry.addButton('attachment', {
                            icon: 'upload',
                            onAction: function (_) {
                                self.$emit('selectUploadFile')
                            }
                        });
                    }
                    if (self.plugins.shortCode) {
                        // editor.ui.registry.addButton('shortCode', {
                        //     icon: 'template',
                        //     onAction: function (_) {
                        //         self.sidebar.shortCodeSelect = true;
                        //     }
                        // });
                    }
                    if (self.plugins.headings) {
                        editor.ui.registry.addIcon('heading-h1-text', `
                            <svg width="24" height="24" viewBox="-144 -128 800 768" xmlns="http://www.w3.org/2000/svg" role="presentation" preserveAspectRatio="xMidYMid meet">
                                <path fill="currentColor" d="M448 96c0-11.1-5.7-21.4-15.2-27.2s-21.2-6.4-31.1-1.4l-64 32c-15.8 7.9-22.2 27.1-14.3 42.9s27.1 22.2 42.9 14.3l17.7-8.8 0 236.2-32 0c-17.7 0-32 14.3-32 32s14.3 32 32 32l128 0c17.7 0 32-14.3 32-32s-14.3-32-32-32l-32 0 0-288zM64 96c0-17.7-14.3-32-32-32S0 78.3 0 96L0 416c0 17.7 14.3 32 32 32s32-14.3 32-32l0-128 128 0 0 128c0 17.7 14.3 32 32 32s32-14.3 32-32l0-320c0-17.7-14.3-32-32-32s-32 14.3-32 32l0 128-128 0 0-128z"/>
                            </svg>
                        `);
                        editor.ui.registry.addIcon('heading-h2-text', `
                            <svg width="24" height="24" viewBox="-108 -96 792 704" xmlns="http://www.w3.org/2000/svg" role="presentation" preserveAspectRatio="xMidYMid meet">
                                <path fill="currentColor" d="M96 96c0-17.7-14.3-32-32-32S32 78.3 32 96l0 320c0 17.7 14.3 32 32 32s32-14.3 32-32l0-128 96 0 0 128c0 17.7 14.3 32 32 32s32-14.3 32-32l0-320c0-17.7-14.3-32-32-32s-32 14.3-32 32l0 128-96 0 0-128zM368 64c-17.7 0-32 14.3-32 32s14.3 32 32 32l81.1 0c21.5 0 38.9 17.4 38.9 38.9 0 13.9-7.5 26.8-19.6 33.8l-76.3 43.6C347.5 269.7 320 317.1 320 368.5l0 47.5c0 17.7 14.3 32 32 32l160 0c17.7 0 32-14.3 32-32s-14.3-32-32-32l-128 0 0-15.5c0-28.4 15.2-54.6 39.9-68.7l76.3-43.6C532.2 237.9 552 203.8 552 166.9 552 110.1 505.9 64 449.1 64L368 64z"/>
                            </svg>
                        `);
                        editor.ui.registry.addIcon('menu-point-bookmark', `
                            <svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" role="presentation">
                                <path d="M7 4.5h10a1 1 0 0 1 1 1v13.75l-6-3.25-6 3.25V5.5a1 1 0 0 1 1-1Z" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" fill="none"/>
                            </svg>
                        `);
                        editor.ui.registry.addButton('headingTitle', {
                            icon: 'heading-h1-text',
                            classes: 'doc-heading-toolbar-button',
                            tooltip: t('Insert heading', 'Ueberschrift einfuegen'),
                            onAction: function () {
                                setHeadingForSelection('h1');
                            }
                        });
                        editor.ui.registry.addButton('headingSubtitle', {
                            icon: 'heading-h2-text',
                            classes: 'doc-heading-toolbar-button',
                            tooltip: t('Insert subheading', 'Unterueberschrift einfuegen'),
                            onAction: function () {
                                setHeadingForSelection('h2');
                            }
                        });
                    }
                    if (self.plugins.menuPoint) {
                        editor.ui.registry.addButton('menuPoint', {
                            icon: 'menu-point-bookmark',
                            classes: 'doc-menu-toolbar-button',
                            tooltip: t('Insert menu point', 'Menuepunkt einfuegen'),
                            onAction: function () {
                                const anchorId = 'doc-menu-anchor-' + Date.now() + '-' + Math.floor(Math.random() * 100000);
                                editor.undoManager.transact(function () {
                                    editor.insertContent('<p><span id="' + anchorId + '" class="doc-menu-anchor-chip">Titel des Menues</span></p><p><br data-mce-bogus="1"></p>');
                                    moveCaretToNewParagraphAfterAnchor(editor.getBody().querySelector('#' + anchorId));
                                });
                            }
                        });

                        editor.on('keydown', function (event) {
                            if ((event.key === 'Enter' || event.keyCode === 13) && isInsideMenuAnchor()) {
                                event.preventDefault();
                                moveCaretToNewParagraphAfterAnchor();
                            }
                        });

                        editor.on('paste', function (event) {
                            if (!isInsideMenuAnchor()) {
                                return;
                            }

                            event.preventDefault();
                            const clipboard = event.clipboardData || window.clipboardData;
                            const text = clipboard ? clipboard.getData('text/plain') : '';
                            editor.insertContent((text || '').replace(/\r?\n+/g, ' '));
                        });

                        editor.on('drop', function (event) {
                            if (!isInsideMenuAnchor()) {
                                return;
                            }

                            event.preventDefault();
                        });

                        editor.on('BeforeSetContent', function (event) {
                            if (!event || typeof event.content !== 'string') {
                                return;
                            }
                            event.content = sanitizeNestedMenuAnchorsInHtml(event.content);
                        });

                        editor.on('SetContent', function () {
                            normalizeMenuAnchors();
                        });

                        editor.on('input', function () {
                            normalizeMenuAnchors();
                        });
                    }
                }
            },
            sidebar: {
                shortCodeSelect: false,
                cannedReplySelect: false,
            }
        }
    },
    methods: {
        getEditorInstance() {
            const editorRef = this.$refs.editor;
            if (editorRef && typeof editorRef.getEditor === 'function') {
                return editorRef.getEditor();
            }
            if (editorRef && editorRef.editor) {
                return editorRef.editor;
            }
            if (window.tinymce && typeof window.tinymce.get === 'function' && this.id) {
                const editorById = window.tinymce.get(this.id);
                if (editorById) {
                    return editorById;
                }
            }
            if (window.tinymce && window.tinymce.activeEditor) {
                return window.tinymce.activeEditor;
            }
            return null;
        },
        insertIntoEditor(content) {
            const editor = this.getEditorInstance();
            if (!editor) {
                return false;
            }
            editor.focus();
            editor.insertContent(content);
            return true;
        },
        uploadFile(e) {
            const self = this;
            if (!e || !e.target || !e.target.files || !e.target.files.length) {
                return;
            }
            const formData = new FormData();
            formData.append('file', e.target.files[0]);
            axios.post('api/files', formData, {headers: {'Content-Type': 'multipart/form-data'}}).then(function (response) {
                self.$refs.imageInput.value = null;
                self.insertIntoEditor('<img src="' + response.data.url + '" width="100%" height="100%" alt="' + response.data.name + '"/>');
            }).catch(function () {
                self.$refs.imageInput.value = null;
            });
        },
        closeSidebar() {
            this.sidebar.shortCodeSelect = false;
            this.sidebar.cannedReplySelect = false;
        },
        setContent(content) {
            this.insertIntoEditor(content);
            this.closeSidebar();
        }
    }
}
</script>

<style lang="scss">
.tox-fullscreen .tox.tox-tinymce.tox-fullscreen {
    z-index: 1040 !important;
}

.editor .tox .tox-toolbar__primary > .tox-toolbar__group:last-child {
    margin-left: auto;
}

.editor .tox .tox-toolbar__primary > .tox-toolbar__group:last-child .tox-tbtn {
    color: var(--ui-text-muted);
}

.wysiwyg-content table {
    width: 100%;
    border-collapse: collapse;
}

.wysiwyg-content table th,
.wysiwyg-content table td {
    border: 1px solid var(--ui-border-default);
    padding: 8px 10px;
    vertical-align: top;
    font-size: 13px;
    line-height: 1.4;
    font-weight: 400;
    text-align: left;
}

.wysiwyg-content table thead th,
.wysiwyg-content table:not(:has(thead)) tbody tr:first-child th,
.wysiwyg-content table:not(:has(thead)) tbody tr:first-child td {
    background: var(--ui-surface-raised);
    font-weight: 400;
    text-align: left;
}

.tox .tox-tbtn.doc-menu-toolbar-button svg path {
    fill: none !important;
    stroke: currentColor !important;
    stroke-width: 1.8 !important;
    stroke-linecap: round !important;
    stroke-linejoin: round !important;
}

</style>
